import React, { useState } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { 
  RefreshCw, 
  Star, 
  Clock, 
  FileText,
  MessageSquare,
  ChevronLeft,
  ChevronRight 
} from 'lucide-react';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { format, formatDistanceToNow } from 'date-fns';
// Since we don't have direct access to update the ExecutionLog type,
// we'll augment it with the properties we need inline
interface ExecutionLog {
  id?: string;
  traceId?: string;
  timestamp: string;
  inputType: string;
  duration: number;
  tokenUsage: number;
  status: string;
  agent_type: string;
  input?: any;
  raw_output?: any;
  parsed_output?: any;
  metadata?: any;
  timing_breakdown?: any;
  prompt?: string;
  customerFeedback?: { rating: number, comment?: string } | null;
  
  // Custom properties for handling data quality
  isMock?: boolean;
  isMockData?: boolean;
  mockReason?: string;
  originalRowData?: any;
}
import EnhancedLogDetailDialog from './EnhancedLogDetailDialog';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { agentService } from '../../lib/api/agent-service';

// Get API URL from localStorage if available, otherwise use environment variable
const API_BASE_URL = localStorage.getItem('api_url') || import.meta.env.VITE_API_URL || 'http://localhost:3001';

interface EnhancedExecutionLogsTableProps {
  logs: ExecutionLog[];
  onRefresh: () => void;
  loading?: boolean;
  agentTypes?: string[];
}

const EnhancedExecutionLogsTable: React.FC<EnhancedExecutionLogsTableProps> = ({ 
  logs, 
  onRefresh, 
  loading = false,
  agentTypes = [] 
}) => {
  const [selectedLog, setSelectedLog] = useState<ExecutionLog | null>(null);
  const [isLogLoading, setIsLogLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterAgentType, setFilterAgentType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const logsPerPage = 10;

  // Get any test logs from localStorage
  const getLocalTestLogs = () => {
    try {
      return JSON.parse(localStorage.getItem('testLogs') || '[]');
    } catch (e) {
      console.error('Failed to load test logs from localStorage', e);
      return [];
    }
  };
  
  // Combine API logs with local test logs
  const combinedLogs = [...logs, ...getLocalTestLogs()];
  
  // Filter logs based on current filters
  const filteredLogs = combinedLogs.filter(log => {
    // Agent type filter
    if (filterAgentType !== 'all' && log.agent_type !== filterAgentType) {
      return false;
    }
    
    // Status filter
    if (filterStatus !== 'all' && log.status !== filterStatus) {
      return false;
    }
    
    // Search query (across multiple fields)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesTraceId = log.traceId?.toLowerCase().includes(query);
      const matchesAgentType = log.agent_type?.toLowerCase().includes(query);
      const matchesInputType = log.inputType?.toLowerCase().includes(query);
      const matchesCustomerMessage = log.input?.customerMessage?.toLowerCase().includes(query);
      
      return matchesTraceId || matchesAgentType || matchesInputType || matchesCustomerMessage;
    }
    
    return true;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredLogs.length / logsPerPage);
  const indexOfLastLog = currentPage * logsPerPage;
  const indexOfFirstLog = indexOfLastLog - logsPerPage;
  const currentLogs = filteredLogs.slice(indexOfFirstLog, indexOfLastLog);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-200">Success</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-800 hover:bg-red-200">Failed</Badge>;
      case 'timeout':
        return <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-200">Timeout</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return {
        formatted: format(date, 'MMM d, yyyy h:mm a'),
        relative: formatDistanceToNow(date, { addSuffix: true })
      };
    } catch (error) {
      return {
        formatted: timestamp,
        relative: 'Unknown'
      };
    }
  };

  /**
   * Checks if a log ID is valid and likely to resolve properly
   * This helps avoid unnecessary network requests for IDs we know will fail
   */
  const isValidLogId = (id: string | undefined): boolean => {
    if (!id) return false;
    
    // MongoDB ObjectIds are 24 hex characters
    if (/^[0-9a-f]{24}$/i.test(id)) return true;
    
    // Trace IDs that start with a specific format are likely valid
    if (id.startsWith('trace-')) return true;
    
    // UUID format (common for trace IDs in distributed systems)
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) return true;
    
    // Other known ID patterns that we expect to work
    return id.length > 8 && !id.includes(' ');
  };
  
  /**
   * Prefetches basic info about a log to determine if it will require mock data
   * This allows us to show an appropriate UI indication before the user clicks
   */
  /**
   * Prefetches basic info about a log to determine if it will require mock data
   * This provides an early indication to users about data quality before clicking
   */
  const getLogDataStatus = (log: ExecutionLog): 'complete' | 'partial' | 'mock' => {
    // If we see isMock flag, we know it's mock data
    if (log.isMock) return 'mock';
    
    // If we have all essential fields, it's likely complete data
    if (log.raw_output && log.input && log.agent_type) return 'complete';
    
    // Check ID validity - IDs of certain formats are more likely to resolve successfully
    const hasValidId = isValidLogId(log.id) || isValidLogId(log.traceId);
    
    // If we have at least some fields + valid ID, we might be able to fetch the rest
    if (hasValidId && (log.agent_type || log.input || log.timestamp)) return 'partial';
    
    // With PostgreSQL, we don't have MongoDB's ObjectId restrictions
    // All ID formats are valid
    
    // If we have a valid ID but no data, it's partial (we'll try to fetch)
    if (hasValidId) return 'partial';
    
    // Otherwise assume we'll need to use mock data
    return 'mock';
  };

  /**
   * Optimized log detail fetching - uses a single API endpoint and improves performance
   * by avoiding multiple cascading requests
   */
  const handleLogClick = async (log: ExecutionLog) => {
    try {
      // Set loading state
      setIsLogLoading(true);
      
      // Always try to fetch complete log details from the server
      // This ensures we have the most up-to-date and complete information
      
      // Prioritize using traceId over log.id to avoid ObjectId validation issues
      const logId = log.traceId || log.id;
      
      // Check if we have a valid ID to query
      if (!logId) {
        console.log('No valid log ID - using enhanced local data only');
        
        // Create a helpful message for the user
        const mockReason = "No log ID available. Using local data only.";
        
        // Create enhanced log with what we have
        const enhancedLog = {
          ...log,
          raw_output: log.raw_output || `Using simplified view. ${mockReason}`,
          parsed_output: log.parsed_output || "Limited log data available.",
          timestamp: log.timestamp || new Date().toISOString(),
          status: log.status || 'unknown',
          traceId: 'unknown',
          mockReason,
          isMock: true
        };
        
        setSelectedLog(enhancedLog);
        setIsLogLoading(false);
        return;
      }
      
      // Try to fetch detailed log using agentService
      try {
        console.log(`Fetching complete log details for ID: ${logId}`);
        const detailedLog = await agentService.getLogById(logId);
        
        if (detailedLog) {
          // Store the original row data and return
          console.log('Successfully retrieved complete log details');
          setSelectedLog({
            ...detailedLog,
            originalRowData: log
          });
          setIsLogLoading(false);
          return;
        }
      } catch (fetchError) {
        console.warn(`Error fetching log with agentService: ${fetchError.message}`);
        // Continue to fallback methods
      }
      
      // Fallback: Try direct API endpoints if agentService failed
      let detailedLog;
      
      // First try the trace endpoint
      if (log.traceId) {
        try {
          const apiUrl = `${API_BASE_URL || 'http://localhost:3001'}/logs/by-trace/${log.traceId}`;
          console.log(`Using trace ID endpoint: ${apiUrl}`);
          
          const response = await fetch(apiUrl);
          if (response.ok) {
            const data = await response.json();
            if (data && data.log) {
              detailedLog = data.log;
            }
          }
        } catch (error) {
          console.warn('Error with trace ID endpoint, trying alternative');
        }
      }
      
      // Next try ObjectId endpoint
      if (!detailedLog && /^[0-9a-f]{24}$/i.test(logId)) {
        try {
          const apiUrl = `${API_BASE_URL || 'http://localhost:3001'}/logs/${logId}`;
          console.log(`Using ObjectId endpoint: ${apiUrl}`);
          
          const response = await fetch(apiUrl);
          if (response.ok) {
            const data = await response.json();
            if (data && data.log) {
              detailedLog = data.log;
            }
          }
        } catch (error) {
          console.warn('Error with ObjectId endpoint, trying alternative');
        }
      }
      
      // Try another fallback endpoint
      if (!detailedLog) {
        try {
          const apiUrl = `${API_BASE_URL || 'http://localhost:3001'}/bypass/log/${logId}`;
          console.log(`Using bypass endpoint: ${apiUrl}`);
          
          const response = await fetch(apiUrl);
          if (response.ok) {
            const data = await response.json();
            if (data && data.log) {
              detailedLog = data.log;
            }
          }
        } catch (error) {
          console.warn('Error with bypass endpoint, will use local data');
        }
      }
      
      // If we got a log, use it; otherwise use what we have with enhancements
      if (detailedLog) {
        // Store the original row data and return
        detailedLog.originalRowData = log;
        console.log('Successfully retrieved log details from fallback endpoint');
        setSelectedLog(detailedLog);
      } else {
        // Use the existing log data with enhancements
        console.log('Using enhanced local data - all API requests failed');
        
        // Create a mock log with appropriate messaging
        const mockLog = {
          ...log,
          raw_output: log.raw_output || `Log details could not be retrieved from the server for ID: ${logId}`,
          parsed_output: log.parsed_output || "Using available data from the logs list.",
          timestamp: log.timestamp || new Date().toISOString(),
          status: log.status || 'unknown',
          mockReason: "Could not retrieve detailed log data from the server.",
          isMock: true,
          originalRowData: log
        };
        
        setSelectedLog(mockLog);
      }
      
    } catch (error) {
      console.error('Error fetching log details:', error);
      
      // Create a simple mock log with error information
      const enhancedLog = {
        ...log,
        raw_output: `Error: ${error instanceof Error ? error.message : 'Unknown error fetching log details'}`,
        parsed_output: "An error occurred while trying to fetch the log details.",
        timestamp: log.timestamp || new Date().toISOString(),
        status: log.status || 'unknown',
        mockReason: "Error occurred during log retrieval.",
        isMock: true
      };
      
      setSelectedLog(enhancedLog);
    } finally {
      setIsLogLoading(false);
    }
  };

  return (
    <>
      <Card className="bg-white border shadow-sm">
        <div className="p-4 border-b flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h3 className="font-medium">Execution Logs</h3>
            <p className="text-sm text-muted-foreground">
              Showing {filteredLogs.length} of {logs.length} logs
            </p>
          </div>
          
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
        
        <div className="p-4 border-b">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search logs..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1); // Reset to first page on search
                }}
                className="w-full"
              />
            </div>
            
            <div className="flex gap-2">
              <Select 
                value={filterAgentType} 
                onValueChange={(value) => {
                  setFilterAgentType(value);
                  setCurrentPage(1); // Reset to first page on filter change
                }}
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Agent Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Agents</SelectItem>
                  {agentTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select 
                value={filterStatus} 
                onValueChange={(value) => {
                  setFilterStatus(value);
                  setCurrentPage(1); // Reset to first page on filter change
                }}
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="success">Success</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="timeout">Timeout</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Agent Type</TableHead>
                  <TableHead>Input Type</TableHead>
                  <TableHead className="text-right">Duration</TableHead>
                  <TableHead className="text-right">Tokens</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Feedback</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {currentLogs.length > 0 ? (
                  currentLogs.map((log) => {
                    const time = formatTimestamp(log.timestamp);
                    const dataStatus = getLogDataStatus(log);
                    
                    // Style the row differently based on data status
                    const rowClassName = `cursor-pointer hover:bg-muted/50 ${
                      dataStatus === 'mock' ? 'opacity-80' : ''
                    }`;
                    
                    return (
                      <TableRow 
                        key={log.id || `log-${time.formatted}`} 
                        onClick={() => handleLogClick(log)}
                        className={rowClassName}
                      >
                        <TableCell>
                          <div className="flex items-center">
                            <div>
                              <div className="font-medium">{time.formatted}</div>
                              <div className="text-xs text-muted-foreground">{time.relative}</div>
                            </div>
                            
                            {/* Data status indicator */}
                            {dataStatus === 'mock' && (
                              <Badge variant="outline" className="ml-2 text-xs bg-yellow-50 border-yellow-200 text-yellow-700">
                                <span className="mr-1">⚠️</span> Mock
                              </Badge>
                            )}
                            {dataStatus === 'partial' && (
                              <Badge variant="outline" className="ml-2 text-xs bg-blue-50 border-blue-200 text-blue-700">
                                <span className="mr-1">ℹ️</span> Limited
                              </Badge>
                            )}
                            {/* Special warning for problematic IDs */}
                            {log.id && !/^[0-9a-f]{24}$/i.test(log.id || '') && !log.id.startsWith('trace-') && (
                              <Badge variant="outline" className="ml-2 text-xs bg-red-50 border-red-200 text-red-700" title="This ID format would cause a BSONError in MongoDB">
                                <span className="mr-1">❌</span> Invalid ID
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{log.agent_type || 'Unknown'}</TableCell>
                        <TableCell>{log.inputType}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end">
                            <Clock className="h-3 w-3 mr-1 text-muted-foreground" />
                            <span>{typeof log.duration === 'number' ? log.duration.toFixed(2) : '0.00'}s</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end">
                            <FileText className="h-3 w-3 mr-1 text-muted-foreground" />
                            <span>{typeof log.tokenUsage === 'number' ? log.tokenUsage.toLocaleString() : '0'}</span>
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(log.status)}</TableCell>
                        <TableCell>
                          {log.customerFeedback ? (
                            <div className="flex items-center">
                              <Star className="h-4 w-4 text-yellow-400 fill-yellow-400 mr-1" />
                              <span>{log.customerFeedback.rating}</span>
                              {log.customerFeedback.comment && (
                                <MessageSquare className="h-4 w-4 ml-1 text-muted-foreground" />
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="h-24 text-center">
                      {filteredLogs.length === 0 && logs.length > 0 ? (
                        <div className="text-muted-foreground">
                          No logs match your filters. 
                          <Button 
                            variant="link" 
                            onClick={() => {
                              setFilterAgentType('all');
                              setFilterStatus('all');
                              setSearchQuery('');
                            }}
                          >
                            Clear filters
                          </Button>
                        </div>
                      ) : loading ? (
                        <div className="flex justify-center items-center">
                          <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                      ) : (
                        <span className="text-muted-foreground">No execution logs found</span>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="py-4 px-6 flex items-center justify-between border-t">
              <div className="text-sm text-muted-foreground">
                Showing {indexOfFirstLog + 1}-{Math.min(indexOfLastLog, filteredLogs.length)} of {filteredLogs.length} logs
              </div>
              
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                
                <div className="flex items-center gap-1 mx-2">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    // Show pages around current page
                    let pageNum;
                    if (totalPages <= 5) {
                      // Show all pages if 5 or fewer
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      // Near beginning
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      // Near end
                      pageNum = totalPages - 4 + i;
                    } else {
                      // In middle
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(pageNum)}
                        className="w-8 h-8 p-0"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Show log detail dialog when a log is selected */}
      <EnhancedLogDetailDialog
        log={selectedLog}
        isOpen={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        isLoading={isLogLoading}
      />
    </>
  );
};

export default EnhancedExecutionLogsTable;